/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { agentsRemoveConnectionBlock } from "../funcs/agentsRemoveConnectionBlock.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RemoveAgentConnectionBlockRequest,
  RemoveAgentConnectionBlockSecurity,
} from "../models/operations/removeagentconnectionblock.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RemoveAgentConnectionBlockMutationVariables = {
  request: RemoveAgentConnectionBlockRequest;
  security?: RemoveAgentConnectionBlockSecurity | undefined;
  options?: RequestOptions;
};

export type RemoveAgentConnectionBlockMutationData = void;

export type RemoveAgentConnectionBlockMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * removeConnectionBlock agents
 *
 * @remarks
 * Remove a block the caller wrote on their own connection. Blocks written by an agent manager are not affected.
 */
export function useRemoveAgentConnectionBlockMutation(
  options?: MutationHookOptions<
    RemoveAgentConnectionBlockMutationData,
    RemoveAgentConnectionBlockMutationError,
    RemoveAgentConnectionBlockMutationVariables
  >,
): UseMutationResult<
  RemoveAgentConnectionBlockMutationData,
  RemoveAgentConnectionBlockMutationError,
  RemoveAgentConnectionBlockMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRemoveAgentConnectionBlockMutation(client, options),
    ...options,
  });
}

export function mutationKeyRemoveAgentConnectionBlock(): MutationKey {
  return ["@gram/client", "agents", "removeConnectionBlock"];
}

export function buildRemoveAgentConnectionBlockMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RemoveAgentConnectionBlockMutationVariables,
  ) => Promise<RemoveAgentConnectionBlockMutationData>;
} {
  return {
    mutationKey: mutationKeyRemoveAgentConnectionBlock(),
    mutationFn: function removeAgentConnectionBlockMutationFn({
      request,
      security,
      options,
    }): Promise<RemoveAgentConnectionBlockMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(agentsRemoveConnectionBlock(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
